import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import ContactSection from '../../components/ContactSection';
import popularCitiesData from '../../data/popularCitiesData.json';
import { MapPin, Car, Users, Clock } from 'lucide-react';

const CityDetail = () => {
  const { cityName } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cityName]);

  const cityData = popularCitiesData?.[cityName?.toLowerCase()];

  if (!cityData) {
    navigate('/popular-cities');
    return null;
  }

  const handleRouteClick = (route) => {
    const routeSlug = route?.toLowerCase()?.replace(/\s+to\s+/gi, '-')?.replace(/\s+/g, '-');
    navigate(`/route/${routeSlug}`);
    window.scrollTo(0, 0);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Advait Tour and Travels - ${cityData?.name}`,
    "description": cityData?.description,
    "telephone": "+91-79986-66660",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": cityData?.name,
      "addressRegion": cityData?.state,
      "addressCountry": "IN"
    },
    "areaServed": {
      "@type": "City",
      "name": cityData?.name
    }
  };

  return (
    <>
      <SEO
        title={`${cityData?.name} Taxi Service - Best Cab Booking in ${cityData?.name} | Advait Tour and Travels`}
        description={`${cityData?.description} Book reliable taxi services in ${cityData?.name} with Advait Tour and Travels. 24/7 availability, experienced drivers, affordable rates.`}
        keywords={`taxi service ${cityData?.name?.toLowerCase()}, cab booking ${cityData?.name?.toLowerCase()}, ${cityData?.name?.toLowerCase()} taxi, ${cityData?.name?.toLowerCase()} cab service, one way taxi ${cityData?.name?.toLowerCase()}`}
        canonicalUrl={`${window.location?.origin}/city-detail/${cityName?.toLowerCase()}`}
        structuredData={structuredData}
        ogTitle={`${cityData?.name} Taxi Service - Best Cab Booking | Advait Tour and Travels`}
        ogDescription={cityData?.description}
        ogUrl={`${window.location?.origin}/city-detail/${cityName?.toLowerCase()}`}
      />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <Header />
        
        {/* Hero Section with Fixed Header Visibility */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 overflow-hidden mt-16 sm:mt-20 md:mt-24">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnpNNiAzNGMzLjMxIDAgNiAyLjY5IDYgNnMtMi42OSA2LTYgNi02LTIuNjktNi02IDIuNjktNiA2LTZ6TTM2IDM0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center py-8 sm:py-12 md:py-16 lg:py-20">
              <div className="text-white space-y-4 sm:space-y-6">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                  {cityData?.name} Taxi Service
                </h1>
                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed">
                  Premium cab booking in {cityData?.name} with Advait Tour and Travels
                </p>
                <div className="flex flex-wrap gap-3 sm:gap-4 pt-2">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300">
                    <Clock size={18} className="sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm font-medium">24/7 Available</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300">
                    <Users size={18} className="sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm font-medium">Experienced Drivers</span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center lg:justify-end">
                <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg">
                  <img
                    src={cityData?.image}
                    alt={cityData?.imageAlt}
                    className="w-full h-auto rounded-lg sm:rounded-xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="py-8 sm:py-12 md:py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            {/* About Section */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 lg:p-10 mb-8 sm:mb-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                About {cityData?.name} Taxi Service
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                {cityData?.description}
              </p>
              
              {cityData?.highlights && cityData?.highlights?.length > 0 && (
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Our Services in {cityData?.name}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {cityData?.highlights?.map((highlight, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-gradient-to-br from-blue-50 to-blue-100 p-3 sm:p-4 rounded-lg hover:shadow-md transition-shadow duration-300">
                        <Car className="text-blue-600 flex-shrink-0 mt-1" size={18} />
                        <span className="text-sm sm:text-base text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Popular Routes Section - Creative & Professional Cards */}
            {cityData?.popularRoutes && cityData?.popularRoutes?.length > 0 && (
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 lg:p-10 mb-8 sm:mb-12 animate-fade-in-up">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center sm:text-left">
                  Popular Routes from {cityData?.name}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
                  {cityData?.popularRoutes?.map((route, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleRouteClick(route)}
                      className="group relative bg-gradient-to-br from-white via-blue-50 to-orange-50 p-5 sm:p-6 rounded-xl sm:rounded-2xl border-2 border-transparent hover:border-blue-400 cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 animate-fade-in overflow-hidden"
                      style={{ animationDelay: `${idx * 0.05}s` }}
                    >
                      {/* Gradient Overlay on Hover */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/0 to-orange-600/0 group-hover:from-blue-600/5 group-hover:to-orange-600/5 transition-all duration-500 rounded-xl sm:rounded-2xl"></div>
                      
                      {/* Content */}
                      <div className="relative z-10 flex items-center gap-3 sm:gap-4">
                        <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                          <MapPin className="text-white" size={20} />
                        </div>
                        <div className="flex-1">
                          <span className="text-sm sm:text-base md:text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300 block">
                            {route}
                          </span>
                          <span className="text-xs sm:text-sm text-gray-500 group-hover:text-gray-700 transition-colors duration-300">View Details →</span>
                        </div>
                      </div>
                      
                      {/* Decorative Elements */}
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-400/10 to-transparent rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-700"></div>
                      <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-400/10 to-transparent rounded-full -ml-8 -mb-8 group-hover:scale-150 transition-transform duration-700"></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cityData?.services && cityData?.services?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10 mb-12">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                  Available Services
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {cityData?.services?.map((service, idx) => (
                    <div key={idx} className="bg-gradient-to-br from-orange-50 to-blue-50 p-4 rounded-lg text-center">
                      <p className="text-gray-800 font-semibold">{service}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>

        <ContactSection />
        <Footer />
      </div>
    </>
  );
};

export default CityDetail;